import { DateTime } from 'luxon'
import { z } from 'zod'

const installationDateFieldRefine = (value: string | null | undefined) =>
  value == null || value === '' || DateTime.fromISO(value).isValid

export const OwnerIdInstallationSchema = z.object({
  ownerId: z.string().min(1, { message: 'common:requiredField' }),
})

const validateSaviooHomeFields = (data: z.infer<typeof CreateInstallationSchema>, ctx: z.RefinementCtx) => {
  if (data.saviooHome) {
    if (!data.zipCode) {
      ctx.addIssue({
        code: 'custom',
        path: ['zipCode'],
        message: 'common:requiredField',
      })
    }
    if (!data.phoneNumberNotifications) {
      ctx.addIssue({
        code: 'custom',
        path: ['phoneNumberNotifications'],
        message: 'common:requiredField',
      })
    }
    if (!data.emailNotifications) {
      ctx.addIssue({
        code: 'custom',
        path: ['emailNotifications'],
        message: 'common:requiredField',
      })
    }
  }
}

export const InstalledSoftwareSchema = z.object({
  softwareType: z.string().optional().nullable(),
  salesSoftware: z.string().optional().nullable(),
  os: z.string().optional().nullable(),
  version: z.string().optional().nullable(),
  softwareId: z.string().optional().nullable(),
  computerName: z.string().optional().nullable(),
  lastUpgradeVersionDateTime: z.string().optional().nullable(),
})
export type InstalledSoftware = z.infer<typeof InstalledSoftwareSchema>

export const MachineTypeSchema = z.object({
  machineId: z.string().min(1),
  quantity: z.number().int().min(1),
})
export type MachineType = z.infer<typeof MachineTypeSchema>

export const CreateInstallationSchema = OwnerIdInstallationSchema.extend({
  installationName: z.string().min(1, { message: 'common:requiredField' }),
  activePlan: z.string().min(1, { message: 'common:requiredField' }),
  nif: z.string().min(1, { message: 'common:requiredField' }),
  country: z.string().min(1, { message: 'common:requiredField' }),
  province: z.string().min(1, { message: 'common:requiredField' }),
  city: z.string().min(1, { message: 'common:requiredField' }),
  street: z.string().min(1, { message: 'common:requiredField' }),
  businessName: z.string().min(1, { message: 'common:requiredField' }),
  timeZone: z.string().min(1, { message: 'common:requiredField' }),
  installationUrl: z.string().optional(),
  workspace: z
    .string()
    .trim()
    .nonempty('common:requiredField')
    .min(5, { message: 'common:minLength' })
    .max(35, { message: 'common:maxWorkspaceLength' })
    .transform((value) => value.toLowerCase())
    .refine((value) => /^[a-z]+$/.test(value), { message: 'common:lettersOnly' }),
  zipCode: z.string().optional().nullable(),
  ipWhiteList: z
    .array(
      z.string().ip({
        version: 'v4',
      }),
    )
    .optional()
    .nullable(),
  saviooHome: z.boolean().optional().default(false),
  phoneNumberNotifications: z.string().optional().nullable(),
  emailNotifications: z.string().optional().nullable(),
  isFacturable: z.boolean().optional().nullable(),
  companyId: z.string().optional().nullable(),
  partnerId: z.string().optional().nullable(),
  odooDeliveryId: z.string().optional().nullable(),
  laboratory: z.string().optional().nullable(),
  saviooCare: z
    .object({
      plan: z.boolean().optional().nullable(),
      discount: z
        .number()
        .min(0, { message: 'common:discountField' })
        .max(100, { message: 'common:discountField' })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
  saviooPlan: z
    .object({
      plan: z.string().optional().nullable(),
      discount: z
        .number()
        .min(0, { message: 'common:discountField' })
        .max(100, { message: 'common:discountField' })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
  patientsPlan: z
    .object({
      plan: z.string().optional().nullable(),
      discount: z
        .number()
        .min(0, { message: 'common:discountField' })
        .max(100, { message: 'common:discountField' })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
  billingStartDate: z.string().nullable().optional(),
  installedSoftwares: z.array(InstalledSoftwareSchema).optional().nullable(),
  machineTypes: z.array(MachineTypeSchema).optional().nullable(),
  status: z.enum(['ACTIVE', 'NOT_ACTIVE']).optional().nullable(),
  sifarmaPharmacyCode: z.string().optional().nullable(),
  installationType: z.enum(['CLIENT', 'DEMO', 'TEST'], { message: 'common:requiredField' }).optional().nullable(),
  startFacturationDate: z.preprocess(
    (val) => (val === '' ? null : val),
    z.string().nullable().optional().refine(installationDateFieldRefine, { message: 'common:invalidDate' }),
  ),
  deactivatedAt: z.preprocess(
    (val) => (val === '' ? null : val),
    z.string().nullable().optional().refine(installationDateFieldRefine, { message: 'common:invalidDate' }),
  ),
})

export const CreateInstallationHomeSchema = CreateInstallationSchema.superRefine(validateSaviooHomeFields)

export const CreateInstallationFacturationSchema = CreateInstallationSchema.superRefine((data, ctx) => {
  if (data.isFacturable) {
    if (!data.companyId || data.companyId === 'NONE') {
      ctx.addIssue({
        code: 'custom',
        path: ['companyId'],
        message: 'common:requiredField',
      })
    }
    if (!data.partnerId) {
      ctx.addIssue({
        code: 'custom',
        path: ['partnerId'],
        message: 'common:requiredField',
      })
    }
    if (!data.odooDeliveryId) {
      ctx.addIssue({
        code: 'custom',
        path: ['odooDeliveryId'],
        message: 'common:requiredField',
      })
    }
    if (!data.laboratory) {
      ctx.addIssue({
        code: 'custom',
        path: ['laboratory'],
        message: 'common:requiredField',
      })
    }
    if (!data.saviooPlan?.plan || data.saviooPlan.plan === 'NONE') {
      ctx.addIssue({
        code: 'custom',
        path: ['saviooPlan', 'plan'],
        message: 'common:requiredField',
      })
    }
    if (!data.patientsPlan?.plan || data.patientsPlan.plan === 'NONE') {
      ctx.addIssue({
        code: 'invalid_type',
        path: ['patientsPlan', 'plan'],
        message: 'common:requiredField',
        expected: 'string',
        received: typeof data.patientsPlan?.plan,
      })
    }
  }
})

export const UpdateInstallationSchema = CreateInstallationFacturationSchema.superRefine(validateSaviooHomeFields)

export type CreateInstallation = z.infer<typeof CreateInstallationFacturationSchema>

export type OwnerIdInstallation = z.infer<typeof CreateInstallationSchema>
